import { Link } from '@tanstack/react-router'
import { Briefcase, Plus, Users } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardBody } from '@/components/ui/card'
import { EmptyState } from '@/components/ui/empty-state'
import { Skeleton } from '@/components/ui/skeleton'
import { JobStatusBadge } from '@/components/ui/status'
import { useMyJobs } from '@/features/marketplace/api'
import { formatRelative, formatRupiah } from '@/lib/format'

export function MyProjectsPage() {
  const { data, isLoading } = useMyJobs()
  const jobs = data?.job_posts ?? []

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-ink sm:text-3xl">Proyek saya</h1>
          <p className="mt-1 text-muted">Kebutuhan yang Anda posting beserta proposal yang masuk.</p>
        </div>
        <Link to="/app/proyek/baru">
          <Button>
            <Plus className="size-4" />
            Posting proyek
          </Button>
        </Link>
      </header>

      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, index) => (
            <Skeleton key={index} className="h-28 rounded-2xl" />
          ))}
        </div>
      ) : jobs.length === 0 ? (
        <Card>
          <EmptyState illustration="box"
            icon={<Briefcase className="size-6" />}
            title="Belum ada proyek"
            description="Tuliskan kebutuhan Anda; tenaga ahli akan mengirim penawaran beserta rencana pengerjaannya."
            action={
              <Link to="/app/proyek/baru">
                <Button>Posting proyek pertama</Button>
              </Link>
            }
          />
        </Card>
      ) : (
        <div className="space-y-3">
          {jobs.map((job) => (
            <Card key={job.id}>
              <CardBody className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <JobStatusBadge status={job.status} />
                    <Badge tone="brand">{job.category}</Badge>
                    <span className="text-xs text-subtle">{formatRelative(job.created_at)}</span>
                  </div>
                  <h2 className="mt-2 font-bold text-ink">{job.title}</h2>
                  <p className="clamp-2 mt-1 text-sm text-muted">{job.description}</p>
                </div>

                <div className="shrink-0 text-right">
                  <p className="font-bold tabular-nums text-ink">
                    {formatRupiah(job.budget_min)} – {formatRupiah(job.budget_max)}
                  </p>
                  <p className="mt-1 inline-flex items-center gap-1 text-sm text-muted">
                    <Users className="size-3.5" />
                    {job.proposal_count} proposal
                  </p>
                  <Link to="/proyek/$jobId" params={{ jobId: job.id }} className="mt-3 block">
                    <Button size="sm" variant={job.proposal_count > 0 ? 'primary' : 'outline'}>
                      {job.proposal_count > 0 ? 'Tinjau proposal' : 'Lihat proyek'}
                    </Button>
                  </Link>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
